import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { useGlobalPageSize } from '../../../utils/tableUtils';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { actions } from '../Users/redux/actions';
import { HEADER_BUTTON_BLUE } from '../../../constants';
import { isCanceledResponse } from '../../../middleware/api';

function AdminOrganizationsTab(props) {
  const globalPageSize = useGlobalPageSize();
  const { getOrganizations, getOrgGroups, onNavigate } = props;

  const [isLoading, setIsLoading] = React.useState(false);
  const [organizations, setOrganizations] = React.useState([]);
  const [orgGroups, setOrgGroups] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedOrgGroup, setSelectedOrgGroup] = React.useState('');
  const [sortColumn, setSortColumn] = React.useState('name');
  const [sortType, setSortType] = React.useState('ascending');
  const [currentPage, setCurrentPage] = React.useState(1);

  // Load org groups for filter
  React.useEffect(() => {
    if (getOrgGroups) {
      getOrgGroups({ limit: 1000 }).then(resp => {
        if (resp?.response) {
          const data = Array.isArray(resp.response) ? resp.response : resp.response.data || [];
          setOrgGroups(data);
        }
      });
    }
  }, [getOrgGroups]);

  const loadOrganizations = React.useCallback(() => {
    if (!getOrganizations) return;

    setIsLoading(true);

    const params = {
      offset: (currentPage - 1) * globalPageSize,
      limit: globalPageSize,
      sortColumn,
      sortType,
    };

    if (searchTerm) params.search = searchTerm;
    if (selectedOrgGroup) params.org_group = selectedOrgGroup;

    getOrganizations(params).then(resp => {
      // Superseded by a newer search/page/sort request, which will fill the table.
      if (isCanceledResponse(resp)) {
        return;
      }
      const data = resp?.response || {};
      const orgsData = Array.isArray(data) ? data : data.data || [];
      setOrganizations(orgsData);
      setPagination({
        totalRecords: data.totalRecords || orgsData.length,
        offset: (currentPage - 1) * globalPageSize,
        limit: globalPageSize,
      });
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  }, [currentPage, globalPageSize, searchTerm, selectedOrgGroup, sortColumn, sortType, getOrganizations]);

  React.useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const onSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const onOffsetChange = (newOffset) => {
    const newPage = Math.floor(newOffset / globalPageSize) + 1;
    setCurrentPage(newPage);
  };

  const onSortingChange = (sortingState) => {
    if (sortingState && sortingState.length > 0) {
      const columnId = sortingState[0].id;
      setSortColumn(columnId);
      setSortType(sortingState[0].desc ? 'descending' : 'ascending');
      setCurrentPage(1);
    }
  };

  const handleRowClick = (rowData) => {
    if (onNavigate) {
      onNavigate(`/admin/organizations/${rowData.uuid || rowData.id}`);
    }
  };

  const columns = React.useMemo(() => [
    columnHelper.accessor('name', {
      header: Strings.name || 'Name',
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('org_group_name', {
      header: Strings.orgGroup || 'Org Group',
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('controls_org_group_name', {
      header: Strings.controlsOrgGroup || 'Controls Org Group',
      enableSorting: false,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('business_state', {
      header: Strings.businessState || 'Business State',
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('user_count', {
      header: Strings.users || 'Users',
      enableSorting: false,
      cell: info => info.getValue() || 0,
    }),
  ], []);

  const getHeaderComponents = () => (
    <>
      <TextInput
        class="search"
        placeholder={Strings.search}
        value={searchTerm}
        onChange={onSearchChange}
      />
      <select 
        value={selectedOrgGroup} 
        onChange={e => { setSelectedOrgGroup(e.target.value); setCurrentPage(1); }}
      >
        <option value="">{Strings.allOrgGroups || 'All Org Groups'}</option>
        {orgGroups.map(group => (
          <option key={group.id || group.uuid} value={group.uuid || group.id}>
            {group.name}
          </option>
        ))}
      </select>
      <Button
        class={HEADER_BUTTON_BLUE}
        onClick={() => onNavigate && onNavigate('/admin/organizations/new')}
        title={`+ ${Strings.create || 'Create'}`}
      />
    </>
  );

  return (
    <>
      <PageHeader title={Strings.organizations || 'Organizations'} right={getHeaderComponents()} />
      <Table3
        data={organizations || []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onSortingChange={onSortingChange}
        onRowClick={handleRowClick}
        noDataText={Strings.noOrganizationsFound || 'No organizations found'}
      />
    </>
  );
}

const mapDispatchToProps = dispatch => ({
  getOrganizations: params => dispatch(actions.getOrganizations(params)),
  getOrgGroups: params => dispatch(actions.getOrgGroups(params)),
  onNavigate: path => dispatch(push(path)),
});

export default connect(null, mapDispatchToProps)(AdminOrganizationsTab);
